import { CardContainer } from '@/components/dashboard';
import Icons from '@/const/icons';
import { ActionRequiredHeader } from './ActionRequiredHeader';
import {
	DotStatus,
	FunnelEmptyScreen,
	Icon,
	LoaderScreen,
	Typography,
} from '@/components/common';
import { useInfiniteQuery } from '@tanstack/react-query';
import { GET_LAWYER_ACTION_REQUIRED } from '@/api';
import { useRouter } from 'next/router';
import { casesLawyerService } from '@/api/casesLawyer';
import { differenceInDays } from 'date-fns';
import {
	ActionRequiredLawyer,
	ActionRequiredLawyerData,
	ActionStatus,
} from '@/interfaces';
import * as React from 'react';
import AppRoutes from '@/const/routes';
import { caseApplicantService } from '@/api/caseApplicant';
import { CaseContext } from '@/context/CaseContext';
import clsx from 'clsx';
import { EmptyStateSearch } from '../../common/EmptyStateSearch';

interface Props {
	notCaseId: string | undefined;
	notDate: string | undefined;
}

export const ActionRequired: React.FC<Props> = ({ notCaseId, notDate }) => {
	const LIMIT_PER_PAGE = 10;
	const router = useRouter();
	const { isCaseReadOnly } = React.useContext(CaseContext);
	const { id, page, search, actionStatusLawyer } = router.query;
	const observer = React.useRef<IntersectionObserver | null>(null);
	const [actionRequiredData, setActionRequiredData] = React.useState<
		ActionRequiredLawyer[]
	>([]);

	const fetchData = async ({
		pageParam,
	}: {
		pageParam: number;
	}): Promise<ActionRequiredLawyerData> => {
		const response = await casesLawyerService.getActionRequired(
			id as string,
			LIMIT_PER_PAGE,
			pageParam,
			search as string,
			actionStatusLawyer?.toString().toUpperCase() as ActionStatus
		);
		return response.data;
	};

	const {
		data: actionRequired,
		refetch,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
	} = useInfiniteQuery({
		initialData: undefined,
		initialPageParam: 1,
		queryKey: [
			GET_LAWYER_ACTION_REQUIRED,
			id,
			page,
			search,
			actionStatusLawyer,
		],
		queryFn: fetchData,
		getNextPageParam(lastPage) {
			return lastPage.page < lastPage.totalPages
				? lastPage.page + 1
				: undefined;
		},
	});

	const lastItemCallback = React.useCallback(
		(node: HTMLDivElement | null) => {
			if (isFetchingNextPage) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting && hasNextPage) {
					fetchNextPage();
				}
			});
			if (node) observer.current.observe(node);
		},
		[isFetchingNextPage, hasNextPage]
	);

	React.useEffect(() => {
		if (actionRequired) {
			const listFlatMapped =
				actionRequired?.pages.flatMap((page) => page.items) || [];
			setActionRequiredData(listFlatMapped);
		}
	}, [actionRequired]);

	React.useEffect(() => {
		if (notCaseId === id) {
			refetch();
		}
	}, [notCaseId, notDate]);

	const handleMarkActionRequired = async (
		application_id: number,
		case_id: string,
		client_id: string
	): Promise<void> => {
		try {
			await caseApplicantService.postCompleteActionRequired(application_id);
			refetch();
			router.push(
				`${AppRoutes.LAWYERAPP_CASES}/${case_id}?tab=case-clients&userId=${client_id}&back=actions-required`
			);
		} catch (error) {
			console.log('Error when unchecking action required');
		}
	};

	return (
		<CardContainer
			title="Action Required"
			classNameTitle="!text-[16px] !leading-[19.2px]"
			icon={Icons.exclamationEmptyState2}
			className={clsx({
				'!bg-gray-20': isCaseReadOnly,
			})}
			iconWidth={73.9}
			iconHeight={31.54}
			classNameContent={clsx('!h-full min-h-[350px]')}
			classNameHeaderContent="!gap-0"
		>
			<div className="flex justify-end w-full mt-1 mb-6">
				<ActionRequiredHeader
					showingItems={actionRequiredData.length || 0}
					totalItems={actionRequired?.pages[0]?.totalItems || 0}
				/>
			</div>
			{isLoading ? (
				<LoaderScreen className="my-10" />
			) : (
				<>
					{actionRequiredData.map((item, index) => {
						const isLastElement = actionRequiredData?.length === index + 1;
						return (
							<div
								ref={isLastElement ? lastItemCallback : null}
								key={'lawyer-action-required-' + item.id + '-' + index}
								className="md:grid grid-cols-4 gap-x-4 justify-between w-full items-center bg-grey-2 rounded-2xl p-2 mb-2"
							>
								<Typography
									type="regular-14"
									className="text-black mb-3 md:mb-0"
								>
									{item.firstname} {item.lastname}
								</Typography>
								<button
									type="button"
									className="flex items-center gap-x-2"
									onClick={() => {
										handleMarkActionRequired(
											item.application_id,
											item.case_id,
											item.id
										);
									}}
								>
									<Typography
										type="regular-16"
										className="text-blue-link underline whitespace-nowrap"
									>
										{item.action_type || 'Review Document'}
									</Typography>
									<Icon
										src={Icons.arrowUpRight}
										className="text-main-blue-dark !size-4 flex-shrink-0"
									/>
								</button>
								<div></div>
								<div className="flex items-center justify-end gap-x-2 mt-3 md:mt-0">
									<DotStatus
										color={
											item.status_color === 'DUE_SOON'
												? 'orange'
												: item.status_color === 'PAST_DUE'
												? 'red'
												: 'yellow'
										}
									/>
									<Typography
										type="semibold-14"
										className="text-black min-w-[180px] text-right"
									>
										{`Submitted ${differenceInDays(
											new Date(),
											item.client_task_end_date
										)} days ago`}
									</Typography>
								</div>
							</div>
						);
					})}
				</>
			)}
			{actionRequired?.pages[0]?.total === 0 && search && (
				<EmptyStateSearch className="my-[88px] mx-auto" />
			)}
			{actionRequired?.pages[0]?.total === 0 &&
				!search &&
				actionStatusLawyer && (
					<FunnelEmptyScreen
						message="There are currently no pending actions matching this filter."
						className="my-[115px]"
					/>
				)}
			{actionRequired?.pages[0]?.total === 0 &&
				!search &&
				!actionStatusLawyer && (
					<div className="m-auto h-[12rem] flex flex-col justify-center bg-gray-20 border border-dashed border-gray-disabled rounded-2xl">
						<Typography
							type="regular-16"
							className="text-center my-10 text-gray-35"
						>
							You have no pending actions.
						</Typography>
					</div>
				)}
		</CardContainer>
	);
};
